import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  CheckCircle2, Upload, AlertTriangle, FileText, ShieldCheck, Truck, Loader2, Camera, CalendarClock,
} from 'lucide-react';
import { toast } from 'sonner';
import PlatformDisclaimer from '@/components/PlatformDisclaimer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOCS = [
  { key: 'w9',  title: 'IRS Form W-9',                 desc: 'Taxpayer Identification (signed)' },
  { key: 'coi', title: 'Certificate of Insurance',     desc: 'Cargo + auto liability with Tempus listed as Certificate Holder' },
  { key: 'ach', title: 'Voided Check / ACH Auth Form', desc: 'For direct payment from brokers' },
];

const CarrierOnboardingPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [coiExpiry, setCoiExpiry] = useState('');
  const fileRefs = useRef({});

  const load = async () => {
    try {
      const r = await axios.get(`${API}/carrier/onboarding/${token}`);
      setData(r.data);
      if (r.data.coi_expiry) setCoiExpiry(r.data.coi_expiry);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const handleUpload = async (docKey, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large (10 MB max)');
      return;
    }
    setUploading(docKey);
    const fd = new FormData();
    fd.append('doc_type', docKey);
    fd.append('file', file);
    if (docKey === 'coi' && coiExpiry) fd.append('coi_expiry', coiExpiry);
    try {
      await axios.post(`${API}/carrier/onboarding/${token}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${docKey.toUpperCase()} uploaded`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(null);
    }
  };

  const onDragEnter = (docKey, e) => { e.preventDefault(); setDragOver(docKey); };
  const onDragLeave = (e) => { e.preventDefault(); setDragOver(null); };
  const onDrop = (docKey, e) => {
    e.preventDefault();
    setDragOver(null);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleUpload(docKey, f);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading onboarding…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md" data-testid="onboarding-invalid">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto text-amber-400 mb-3" />
            <h2 className="text-xl font-bold mb-2">Invalid or expired link</h2>
            <p className="text-muted-foreground text-sm">Please request a fresh onboarding link from your dispatcher.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { carrier, status, have, missing, documents,
          coi_expiring_soon, coi_expired, coi_days_to_expiry } = data;
  const complete = status === 'complete';

  const coiBanner = () => {
    if (coi_expired) return (
      <div className="text-[11px] text-red-300 mt-1" data-testid="coi-expired-flag">
        ⚠ COI expired {Math.abs(coi_days_to_expiry)} day{Math.abs(coi_days_to_expiry) === 1 ? '' : 's'} ago — please re-upload
      </div>
    );
    if (coi_expiring_soon) return (
      <div className="text-[11px] text-amber-300 mt-1" data-testid="coi-expiring-soon-flag">
        ⏰ COI expires in {coi_days_to_expiry} day{coi_days_to_expiry === 1 ? '' : 's'}
      </div>
    );
    if (data.coi_expiry) return (
      <div className="text-[11px] text-green-400 mt-1">Expires {data.coi_expiry}</div>
    );
    return null;
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="carrier-onboarding-page">
      <div className="flex-1 py-10 px-4">
        <div className="max-w-2xl mx-auto space-y-5">
          <div className="text-center">
            <Truck className="w-8 h-8 mx-auto text-primary mb-2" />
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Carrier Onboarding · Tempus Dispatch</p>
          </div>

          <Card data-testid="carrier-summary-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-xl">{carrier.company_name || carrier.name}</CardTitle>
                  <p className="text-xs text-muted-foreground">{carrier.email}</p>
                </div>
                <Badge
                  data-testid="onboarding-status-badge"
                  className={complete
                    ? 'bg-green-500/20 text-green-300 border-green-500/30'
                    : 'bg-amber-500/20 text-amber-300 border-amber-500/30'}
                >
                  {complete ? 'PACKET COMPLETE' : `${have.length}/3 ON FILE`}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground space-y-1">
              {carrier.mc_number && <div>MC #{carrier.mc_number}</div>}
              {carrier.dot_number && <div>DOT #{carrier.dot_number}</div>}
              {!complete && missing.length > 0 && (
                <div className="pt-2 text-amber-300">
                  Still needed: {missing.map((m) => m.toUpperCase()).join(', ')}
                </div>
              )}
            </CardContent>
          </Card>

          {DOCS.map((doc) => {
            const meta = documents[doc.key];
            const uploaded = !!meta;
            const isCoi = doc.key === 'coi';
            const isDragging = dragOver === doc.key;
            return (
              <Card
                key={doc.key}
                data-testid={`doc-row-${doc.key}`}
                onDragEnter={(e) => onDragEnter(doc.key, e)}
                onDragOver={(e) => { e.preventDefault(); }}
                onDragLeave={onDragLeave}
                onDrop={(e) => onDrop(doc.key, e)}
                className={`transition-colors ${isDragging ? 'border-blue-500 bg-blue-500/5' : ''}`}
              >
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0
                      ${uploaded ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-zinc-300'}`}>
                      {uploaded ? <CheckCircle2 className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold">{doc.title}</div>
                      <div className="text-xs text-muted-foreground">{doc.desc}</div>
                      {uploaded && (
                        <div className="text-[11px] text-green-400 mt-1 truncate">
                          ✓ {meta.filename} ({Math.round((meta.size || 0) / 1024)} KB)
                        </div>
                      )}
                      {isCoi && uploaded && coiBanner()}
                      {isDragging && (
                        <div className="text-[11px] text-blue-400 mt-1">Drop your file to upload…</div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5">
                      {/* Plain file picker (drag-drop target + click) */}
                      <input
                        ref={(el) => (fileRefs.current[`${doc.key}-file`] = el)}
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.heic,.heif,.webp"
                        className="hidden"
                        data-testid={`upload-input-${doc.key}`}
                        onChange={(e) => handleUpload(doc.key, e.target.files?.[0])}
                      />
                      {/* Mobile camera capture — rear camera */}
                      <input
                        ref={(el) => (fileRefs.current[`${doc.key}-cam`] = el)}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        data-testid={`camera-input-${doc.key}`}
                        onChange={(e) => handleUpload(doc.key, e.target.files?.[0])}
                      />
                      <Button
                        size="sm"
                        variant={uploaded ? 'outline' : 'default'}
                        onClick={() => fileRefs.current[`${doc.key}-file`]?.click()}
                        disabled={uploading === doc.key}
                        data-testid={`upload-button-${doc.key}`}
                      >
                        {uploading === doc.key ? (
                          <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Uploading</>
                        ) : (
                          <><Upload className="w-4 h-4 mr-1" /> {uploaded ? 'Replace' : 'Upload'}</>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="sm:hidden text-xs"
                        onClick={() => fileRefs.current[`${doc.key}-cam`]?.click()}
                        disabled={uploading === doc.key}
                        data-testid={`camera-button-${doc.key}`}
                      >
                        <Camera className="w-4 h-4 mr-1" /> Photo
                      </Button>
                    </div>
                  </div>

                  {/* COI expiry date picker */}
                  {isCoi && (
                    <div className="flex items-center gap-2 pl-14 pr-1">
                      <CalendarClock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <Label htmlFor={`coi-expiry-${doc.key}`} className="text-[11px] text-muted-foreground shrink-0">
                        COI expiration (optional)
                      </Label>
                      <Input
                        id={`coi-expiry-${doc.key}`}
                        type="date"
                        value={coiExpiry}
                        onChange={(e) => setCoiExpiry(e.target.value)}
                        className="h-7 text-xs max-w-[160px]"
                        data-testid="coi-expiry-input"
                      />
                      <span className="text-[10px] text-muted-foreground italic">
                        sent with next upload
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {complete && (
            <Card className="border-green-500/40 bg-green-500/10" data-testid="onboarding-complete-banner">
              <CardContent className="p-5 text-center flex items-center justify-center gap-2">
                <ShieldCheck className="w-5 h-5 text-green-400" />
                <span className="text-sm text-green-200">
                  All three documents on file. Brokers will see "Packet Complete" on your profile.
                </span>
              </CardContent>
            </Card>
          )}

          <p className="text-[11px] text-muted-foreground text-center">
            PDFs, JPG, PNG, or HEIC — 10 MB max per file. On mobile, tap "Photo" to capture with your camera. On desktop, drop a file directly into any row.
          </p>
        </div>
      </div>
      <PlatformDisclaimer />
    </div>
  );
};

export default CarrierOnboardingPage;
